import React, { useEffect } from 'react';
import { motion, useAnimation, useReducedMotion } from 'framer-motion';
import styled from 'styled-components';

const AudioButton = styled(motion.button)`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: var(--color-portfolio-wine);
  border: 2px solid var(--color-portfolio-silver);
  cursor: pointer;
  z-index: 1000;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  transition: background-color 0.1s ease-out;

  &:focus-visible {
    outline: 2px solid var(--color-portfolio-silver);
    outline-offset: 4px;
    box-shadow: 0 0 0 6px var(--color-portfolio-wine);
  }

  @media (hover: hover) {
    &:hover {
      background: var(--color-portfolio-ink);
    }
  }
`;

const WaveAnimation = styled.div`
  display: flex;
  align-items: center;
  gap: 3px;
  height: 20px;
`;

const Wave = styled(motion.div)`
  width: 3px;
  background: var(--color-portfolio-silver);
  border-radius: 2px;
`;

const AudioPlayer = ({ isPlaying, setIsPlaying, audioRef }) => {
  const controls = useAnimation();
  const reduceMotion = useReducedMotion();

  useEffect(() => {
    if (isPlaying && !reduceMotion) {
      controls.start('playing');
    } else {
      controls.start('paused');
    }
  }, [isPlaying, controls, reduceMotion]);

  const togglePlay = async () => {
    try {
      if (!audioRef.current) return;

      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        await audioRef.current.play();
        setIsPlaying(true);
      }
    } catch (error) {
      setIsPlaying(false);
      console.error('Toggle play error:', error);
    }
  };

  const waveVariants = {
    playing: (i) => ({
      height: [5, 20, 5],
      transition: {
        duration: 0.8,
        repeat: Infinity,
        repeatType: "reverse",
        ease: "easeInOut",
        delay: i * 0.2,
      },
    }),
    paused: {
      height: 10,
      transition: {
        duration: 0.3,
      },
    },
  };

  return (
    <AudioButton
      onClick={togglePlay}
      initial={reduceMotion ? false : { scale: 0, rotate: -180 }}
      animate={{
        scale: 1, 
        rotate: 0,
        transition: reduceMotion
          ? { duration: 0 }
          : {
              type: "spring",
              stiffness: 260,
              damping: 20,
            }
      }}
      whileHover={reduceMotion ? undefined : { scale: 1.1 }}
      whileTap={reduceMotion ? undefined : { scale: 0.9 }}
      aria-label={isPlaying ? 'Pause music' : 'Play music'}
    >
      <WaveAnimation>
        {[...Array(3)].map((_, i) => (
          <Wave
            key={i}
            custom={i}
            variants={waveVariants}
            animate={controls}
          />
        ))}
      </WaveAnimation>
    </AudioButton>
  );
};

export default AudioPlayer; 
